import React, { useEffect, useState } from "react";
import axios from "axios";
import { HERO, BOOKING } from "@/constants/testIds";
import { useSiteContent } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STOCK_HERO_1 =
  "https://images.pexels.com/photos/14180701/pexels-photo-14180701.jpeg?auto=compress&cs=tinysrgb&w=900";
const STOCK_HERO_2 =
  "https://images.pexels.com/photos/7222093/pexels-photo-7222093.jpeg?auto=compress&cs=tinysrgb&w=900";

export const Hero = () => {
  const { content } = useSiteContent();
  const [remaining, setRemaining] = useState(null);
  const [limit, setLimit] = useState(content.free_slot_limit ?? 5);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/free-slots`).then((res) => {
      if (!active) return;
      setRemaining(res.data.remaining);
      setLimit(res.data.limit);
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, [content.free_slot_limit]);

  const img1 = content.hero_image_primary || STOCK_HERO_1;
  const img2 = content.hero_image_secondary || STOCK_HERO_2;
  const stats = content.hero_stats || [];

  return (
    <section id="top" className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 grain" />
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#F4EFE6] blur-3xl opacity-70" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 anim-fade-up">
          <div
            data-testid={HERO.badgeFree}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#CBA358] text-[#0A2342] rounded-full text-[11px] uppercase tracking-[0.22em] font-bold animate-slow-pulse"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-[#0A2342]" />
            Free for the first {remaining ?? limit} students
          </div>

          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl text-[#0A2342] font-bold leading-[1.05] text-balance">
            {content.hero_headline}
          </h1>

          <p className="mt-7 max-w-xl text-lg text-[#5C636A] leading-relaxed font-body text-pretty">
            {content.hero_subheadline}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#booking"
              data-testid={HERO.primaryCta}
              className="inline-flex items-center gap-2 bg-[#CBA358] text-[#0A2342] px-7 py-4 text-sm uppercase tracking-wider font-bold shadow-[4px_4px_0px_#0A2342] hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A2342] transition-all"
            >
              {content.hero_cta_primary}
            </a>
            <a
              href={content.google_form_url || "#assessment"}
              target="_blank"
              rel="noreferrer"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center gap-2 border-2 border-[#0A2342] text-[#0A2342] px-7 py-4 text-sm uppercase tracking-wider font-medium hover:bg-[#0A2342] hover:text-white transition-colors"
            >
              {content.hero_cta_secondary} →
            </a>
          </div>

          {stats.length > 0 && (
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#5C636A]">
              {stats.map((s, i) => (
                <React.Fragment key={i}>
                  <span><strong className="text-[#0A2342]">{s}</strong></span>
                  {i < stats.length - 1 && <span className="w-px h-4 bg-[#E5DFD3]" />}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] max-w-md mx-auto">
            <div className="absolute -top-4 -left-4 w-full h-full bg-[#0A2342]" />
            <img src={img1} alt="Hero" loading="lazy" className="relative w-full h-full object-cover" />
            <div className="absolute -bottom-10 -right-10 w-44 h-44 bg-[#CBA358] hidden sm:block" />
            <img
              src={img2}
              alt="Counselling"
              loading="lazy"
              className="absolute -bottom-14 -right-6 w-40 h-52 object-cover border-8 border-[#FDFBF7] hidden sm:block"
            />
          </div>

          <div
            data-testid={BOOKING.spotsRemaining}
            className="absolute top-4 right-4 sm:-top-2 sm:right-0 bg-white border border-[#E5DFD3] px-4 py-3 max-w-[180px] shadow-sm"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C636A]">Free Slots</div>
            <div className="font-display text-3xl font-bold text-[#0A2342] leading-none mt-1">
              {remaining ?? limit}<span className="text-[#CBA358]">/{limit}</span>
            </div>
            <div className="text-[11px] text-[#5C636A] mt-1">remaining</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
